import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Boxes, Truck, Store, AlertCircle } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";

// Phase C/E: Salesman read-only "My Stock" view for today.
export default function MyStock() {
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stock/today")
      .then(r => setRec(r.data || null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7]">
        <TopBar title={t("stock_balance")} onBack={() => nav("/salesman")} />
        <div className="text-center text-[#86868B] py-10">{t("loading")}</div>
      </div>
    );
  }

  if (!rec) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] page-slide-in">
        <TopBar title={t("stock_balance")} onBack={() => nav("/salesman")} />
        <div className="max-w-2xl mx-auto p-4 sm:p-6 fade-up">
          <div className="bg-white rounded-3xl p-10 text-center soft-shadow" data-testid="mystock-empty">
            <AlertCircle className="w-12 h-12 mx-auto text-[#86868B] mb-3" />
            <div className="heading-font font-bold text-lg text-[#1D1D1F]">
              {lang === "ta" ? "இன்று ஸ்டாக் ஏற்றம் இல்ல" : "No stock loaded today"}
            </div>
            <div className="text-sm text-[#86868B] mt-2">
              {lang === "ta"
                ? "உங்கள் மேனேஜர் காலையில் ஸ்டாக் ஏற்றியதும் இங்கே தோன்றும்."
                : "Your manager will load stock from depot in the morning."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totals = rec.totals || {};

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10 page-slide-in">
      <TopBar title={t("stock_balance")} onBack={() => nav("/salesman")} />
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-5 fade-up">
        {/* Hero summary */}
        <div className="bg-gradient-to-br from-[#0A1A3F] to-[#0055FF] text-white rounded-3xl p-6 shadow-xl" data-testid="mystock-hero">
          <div className="flex items-center gap-2 mb-4">
            <Truck className="w-5 h-5" />
            <div className="text-xs uppercase tracking-[0.2em] font-bold opacity-80">
              {rec.depot_name || (lang === "ta" ? "டிப்போ" : "Depot")} • {rec.date}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div data-testid="mystock-loaded">
              <div className="text-[10px] uppercase tracking-wider opacity-70">{lang === "ta" ? "ஏற்றம்" : "Loaded"}</div>
              <div className="heading-font text-3xl font-bold">{totals.loaded || 0}</div>
            </div>
            <div data-testid="mystock-sold">
              <div className="text-[10px] uppercase tracking-wider opacity-70">{lang === "ta" ? "விற்றது" : "Sold"}</div>
              <div className="heading-font text-3xl font-bold">{totals.sold || 0}</div>
            </div>
            <div data-testid="mystock-remaining">
              <div className="text-[10px] uppercase tracking-wider opacity-70">{lang === "ta" ? "மீதம்" : "Remaining"}</div>
              <div className="heading-font text-3xl font-bold text-yellow-300">{totals.remaining || 0}</div>
            </div>
          </div>
          <div className="text-xs opacity-70 mt-4">
            {lang === "ta" ? "ஏற்றம் செய்தவர்" : "Loaded by"}: {rec.loaded_by_name || "—"}
          </div>
        </div>

        {/* Per-product list */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <h2 className="heading-font font-bold text-lg flex items-center gap-2">
              <Boxes className="w-5 h-5 text-[#0055FF]" /> {t("products")}
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {rec.items.map(it => (
              <div key={it.product_id} className="p-4 sm:p-5 flex items-center gap-3" data-testid={`mystock-row-${it.product_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold heading-font text-[#1D1D1F]">
                    {lang === "ta" ? (it.product_name_ta || it.product_name) : it.product_name}
                  </div>
                  <div className="text-xs text-[#86868B] mt-0.5">
                    {lang === "ta"
                      ? `ஏற்றம் ${it.loaded_qty} • விற்றது ${it.sold_qty}`
                      : `Loaded ${it.loaded_qty} • Sold ${it.sold_qty}`}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="heading-font text-2xl font-bold text-[#FF9500]">{it.remaining_qty}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{t("boxes")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
